// packages/web/src/components/LearnerView.tsx — misma lógica (búsqueda por email exacto, auto-selección, perfil + summary).
// Cambios: encabezado común, ficha del alumno con acción principal, resumen en cifras, historial en tabla,
// notas Pioneros y reportes previos plegables. Hoy oculta del menú; queda lista si se reactiva.
import { useEffect, useRef, useState } from 'react';
import { Search, Loader2, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import { api, type LearnerProfile, type LearnerProfileFull } from '../api';
import { DICTAMEN_LABEL, DIM_LABEL, type LearnerMarzanoSummary, type SessionListItem } from '../types';
import { Alert, Button, Card, INPUT_CLASS, PageHeader, Stat, StatusBadge, TABLE } from './ui';

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (s: string): boolean => EMAIL_RX.test(s);
const SEARCH_DEBOUNCE_MS = 350;
const DICT_TONE: Record<string, 'danger' | 'warn' | 'ok' | 'info'> = { no_evidencia: 'danger', en_desarrollo: 'warn', suficiente: 'ok', profundo: 'info' };

interface LearnerViewProps {
  siteId: string;
  onStartPilot: (learner: LearnerProfileFull) => void;
  onOpenPilot: (sessionId: string) => void;
  onClose: () => void;
}

export function LearnerView(props: LearnerViewProps) {
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<LearnerProfileFull[]>([]);
  const [selected, setSelected] = useState<LearnerProfileFull | null>(null);
  const [profile, setProfile] = useState<LearnerProfile | null>(null);
  const [summary, setSummary] = useState<LearnerMarzanoSummary | null>(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notasExpanded, setNotasExpanded] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (!isValidEmail(trimmed)) { setMatches([]); return; }
    debounceRef.current = window.setTimeout(async () => {
      setLoadingSearch(true); setError(null);
      try {
        const r = await api.searchLearners(trimmed, props.siteId);
        setMatches(r.matches);
        if (r.matches.length === 1) void selectMatch(r.matches[0]!);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoadingSearch(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current !== null) window.clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, props.siteId]);

  async function selectMatch(m: LearnerProfileFull) {
    setSelected(m); setProfile(null); setSummary(null); setNotasExpanded(false); setLoadingProfile(true);
    try {
      const [full, sum] = await Promise.all([api.getLearnerProfile(m.email, props.siteId), api.getLearnerMarzanoSummary(m.email, props.siteId).catch(() => null)]);
      setProfile(full);
      if (sum) setSummary(sum.summary);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingProfile(false);
    }
  }
  function startPilotFromSelected() { if (selected) props.onStartPilot(selected); }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <PageHeader back={{ label: 'Cerrar', onClick: props.onClose }} title="Buscar alumno" subtitle="Escribe el email exacto del alumno para ver su perfil y sus evaluaciones." />

      <div className="max-w-xl">
        <label htmlFor="lv-q" className="block text-sm font-medium text-slate-800">Email del alumno</label>
        <div className="relative">
          <Search aria-hidden className="w-4 h-4 text-slate-500 absolute left-3 top-[calc(50%+3px)] -translate-y-1/2" />
          <input id="lv-q" type="email" className={INPUT_CLASS + ' pl-9 pr-9'} placeholder="j.ossa.15@cpioneros.cl" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          {loadingSearch && <Loader2 aria-label="Buscando" className="w-4 h-4 animate-spin text-slate-500 absolute right-3 top-[calc(50%+3px)] -translate-y-1/2" />}
        </div>
        {q.trim() && !isValidEmail(q.trim()) && <p className="text-[13px] text-slate-600 mt-1.5">Escribe el email completo.</p>}
        {isValidEmail(q.trim()) && matches.length === 0 && !loadingSearch && <Alert kind="warn" className="mt-2">No encontramos ese email en Pioneros. Revisa que esté bien escrito.</Alert>}
        {error && <Alert kind="error" className="mt-2">{error}</Alert>}
      </div>

      {selected && (
        <>
          <section className="rounded-xl border border-slate-200 bg-white p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <StatusBadge tone="ok">{selected.privileges === 'learner' ? 'Alumno Pioneros' : selected.privileges ?? 'Usuario'}</StatusBadge>
              <h2 className="text-xl font-semibold text-slate-900 mt-1.5">{selected.name ?? '(sin nombre)'}</h2>
              <p className="text-sm text-slate-600">{selected.email}{selected.level_label && <> · <span className="text-marzano-700 font-medium">{selected.level_label}</span></>}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setProfile(null); setQ(''); }}>Buscar otro</Button>
              <Button onClick={startPilotFromSelected} rightIcon={<ArrowRight aria-hidden className="w-4 h-4" />}>Iniciar evaluación</Button>
            </div>
          </section>

          {loadingProfile && <p className="flex items-center gap-2 text-sm text-slate-700"><Loader2 aria-hidden className="w-4 h-4 animate-spin" />Cargando historial…</p>}

          {summary && summary.total_pilots > 0 && <MarzanoSummary summary={summary} />}
          {profile && profile.recent_pilots.length > 0 && <PreviousPilots pilots={profile.recent_pilots} onOpenPilot={props.onOpenPilot} />}

          {profile?.learner_context && profile.learner_context.recent_grades_summary.length > 0 && (
            <section className="rounded-xl border border-slate-200 bg-white">
              <button type="button" aria-expanded={notasExpanded} onClick={() => setNotasExpanded((v) => !v)} className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left">
                <span className="text-sm font-semibold text-slate-900">Notas recientes en Pioneros <span className="font-normal text-slate-600">· {profile.learner_context.recent_grades_summary.length}</span></span>
                {notasExpanded ? <ChevronDown aria-hidden className="w-4 h-4 text-slate-500" /> : <ChevronRight aria-hidden className="w-4 h-4 text-slate-500" />}
              </button>
              {notasExpanded && <ul className="px-5 pb-4 space-y-1 text-sm text-slate-800 list-disc pl-9 marker:text-slate-400">{profile.learner_context.recent_grades_summary.map((g, i) => <li key={i}>{g}</li>)}</ul>}
            </section>
          )}

          {profile?.learner_context && profile.learner_context.prior_dictamenes.length > 0 && (
            <Card title="Informes cualitativos previos">
              <ul className="space-y-2">
                {profile.learner_context.prior_dictamenes.map((d, i) => (
                  <li key={i} className="rounded-lg bg-slate-50 px-3.5 py-2.5">
                    <span className="font-mono text-[13px] font-semibold text-marzano-700">{d.oa_codigo}</span>
                    <p className="text-sm text-slate-800 mt-0.5">{d.summary}</p>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function MarzanoSummary({ summary }: { summary: LearnerMarzanoSummary }) {
  const dominantState = (() => {
    const entries = Object.entries(summary.state_breakdown) as Array<[string, number]>;
    if (entries.length === 0) return null;
    return entries.sort((a, b) => b[1] - a[1])[0]![0];
  })();
  return (
    <section aria-labelledby="ms-t" className="space-y-3">
      <h2 id="ms-t" className="text-base font-semibold text-slate-900">Resumen Marzano <span className="font-normal text-[13px] text-slate-600">· solo evaluaciones de este sistema</span></h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Evaluaciones" value={summary.total_pilots} hint={summary.total_closed_with_dictamen > 0 ? `${summary.total_closed_with_dictamen} con reporte` : undefined} />
        {dominantState && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5">
            <div className="text-[13px] text-slate-600">Estado más frecuente</div>
            <div className="mt-1.5"><StatusBadge tone={DICT_TONE[dominantState] ?? 'muted'}>{DICTAMEN_LABEL[dominantState as keyof typeof DICTAMEN_LABEL] ?? dominantState}</StatusBadge></div>
            {summary.avg_global_score !== null && <div className="text-xs text-slate-600 mt-1">promedio {summary.avg_global_score.toFixed(0)}/100</div>}
          </div>
        )}
        {summary.most_frequent_weak_dim && <Stat label="Dimensión más débil" value={<span className="text-lg">{DIM_LABEL[summary.most_frequent_weak_dim]}</span>} hint="se repite entre evaluaciones" />}
        {summary.last_evaluated_at && <Stat label="Última evaluación" value={<span className="text-lg">{new Date(summary.last_evaluated_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })}</span>} />}
      </div>
      {summary.by_subject.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Por asignatura">
          {summary.by_subject.map((s) => (
            <li key={s.asignatura} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px]">
              <span className="font-semibold text-slate-900">{s.asignatura}</span>
              <span className="text-slate-600"> · {s.count} eval.{s.avg_global_score !== null && ` · prom. ${s.avg_global_score.toFixed(0)}`}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PreviousPilots({ pilots, onOpenPilot }: { pilots: SessionListItem[]; onOpenPilot: (sessionId: string) => void }) {
  return (
    <section aria-labelledby="pp-t" className="space-y-3">
      <h2 id="pp-t" className="text-base font-semibold text-slate-900">Evaluaciones previas · {pilots.length}</h2>
      <div className={TABLE.wrap}>
        <table className={TABLE.table + ' min-w-[640px]'}>
          <thead className={TABLE.thead}>
            <tr><th className={TABLE.th}>Fecha</th><th className={TABLE.th}>OA</th><th className={TABLE.th}>Estado</th><th className={TABLE.th + ' text-right'}>Puntaje</th><th className={TABLE.th}>Dim. débil</th><th className={TABLE.th}>Modalidad</th><th className={TABLE.th}><span className="sr-only">Abrir</span></th></tr>
          </thead>
          <tbody>
            {pilots.map((p) => {
              const oa = (p.oa_snapshot as { oa_codigo?: string }).oa_codigo ?? '?';
              return (
                <tr key={p.id} className={TABLE.tr + ' cursor-pointer'} onClick={() => onOpenPilot(p.id)}>
                  <td className={TABLE.td + ' text-slate-700 whitespace-nowrap'}>{new Date(p.started_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}</td>
                  <td className={TABLE.td}><span className="font-mono text-[13px] font-semibold text-marzano-700 bg-marzano-50 rounded px-1.5 py-0.5">{oa}</span></td>
                  <td className={TABLE.td}>{p.dictamen_state ? <StatusBadge tone={DICT_TONE[p.dictamen_state] ?? 'muted'}>{DICTAMEN_LABEL[p.dictamen_state]}</StatusBadge> : <span className="text-[13px] text-slate-600">Sin reporte</span>}</td>
                  <td className={TABLE.td + ' text-right tabular-nums font-semibold'}>{p.dictamen_global_score !== null ? p.dictamen_global_score.toFixed(0) : '—'}</td>
                  <td className={TABLE.td + ' text-slate-800'}>{p.weakest_dim ? DIM_LABEL[p.weakest_dim] : '—'}</td>
                  <td className={TABLE.td + ' text-slate-700'}>{p.modality}</td>
                  <td className={TABLE.td + ' text-right'}>
                    <button type="button" aria-label={`Abrir evaluación ${oa}`} onClick={(e) => { e.stopPropagation(); onOpenPilot(p.id); }} className="p-1 rounded text-slate-500 hover:text-marzano-600 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500">
                      <ArrowRight aria-hidden className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
